import { useMemo, useState } from 'react'
import EstoqueHttp from '../../../../../../services/api/https/estoque/Estoque.http'
import { errorHandler } from '../../../../../../services/util/error.service'
import { parseToMysqlFormat } from '../../../../../../services/util/date.service'
import { gerarArquivoExcel } from '../../../../../../services/util/excel.service'
import { orderBy } from 'lodash'
import {
  formatVolumeAndUnits,
  sumVolumeAndUnits,
} from '../../../../../../services/util/volume-and-units.service'
import {
  buildInventarioQuantityInputState,
  formatInventarioQuantity,
  getInventarioQuantityValueFromInputs,
} from '../../inventario-volume-and-units.helper'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  GET_POSICOES: 'Carregando posições',
  ACERTO: 'Efetuando acerto da posições',
  LOADING: 'Carregando',
  NOVA_CONFERENCIA: 'Gerando nova conferencia da posição',
  OK_CLICK_ACERTO: 'Carregando acerto',
}

const INITIAL_STATE = {
  inventarioFinished: false,
  inventario: null,
  posicoes: [],
  totalFaltas: 0,
  totalSobras: 0,
  totalFaltasExibicao: '',
  totalSobrasExibicao: '',
  operacoesEntrada: [],
  operacoesSaida: [],
  selectedDefaultOpSaida: null,
  selectedDefaultOpEntrada: null,
  activeIndex: '',
  selectedPosicoes: [],
  pathToRedirect: null,
  globalFilter: '',
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  showDialogConfirm: false,
}

export default function useAnaliseResultado() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    //Helpers
    const buildTotalVolumeAndUnitsExibicao = items => {
      return formatVolumeAndUnits(
        sumVolumeAndUnits(items, i => ({
          quantity: i.qte_dif,
          unitSku: i.unit_sku,
          caixaComPesoVariavel: i.caixa_com_peso_variavel,
        })),
      )
    }

    return {
      getPosicoesWithLastConferences(selectedTp, selectedInventarioId) {
        EstoqueHttp.getPosicoesWithLastConferenciaFromInventario(selectedTp, selectedInventarioId)
          .then(res => {
            const posicoes = res.posicoes
            posicoes.forEach(r =>
              r.items.forEach(p => {
                p.qte_ajuste = p.qte_dif
                p.operacao_id = 0
                const splitState = buildInventarioQuantityInputState(p, Math.abs(p.qte_dif))
                p.qte_ajuste_volume = splitState.volumeValue
                p.qte_ajuste_frac = splitState.unitsValue
              }),
            )
            const items = posicoes.reduce((prev, p) => prev.concat(p.items || []), [])
            setState(prevState => ({
              ...prevState,
              totalFaltas: res.total_faltas,
              totalSobras: res.total_sobras,
              totalFaltasExibicao: buildTotalVolumeAndUnitsExibicao(
                items.filter(i => i.qte_dif < 0),
              ),
              totalSobrasExibicao: buildTotalVolumeAndUnitsExibicao(
                items.filter(i => i.qte_dif > 0),
              ),
              posicoes: posicoes,
              selectedPosicoes: [],
              activeIndex: posicoes.map((p, i) => i),
              loadingMessage: LOAD_MESSAGE.NONE,
            }))
          })
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      getOperacoesEntrada(selectedTp) {
        EstoqueHttp.getOperacoesEstoqueEntrada(selectedTp)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              operacoesEntrada: res.filter(r => r.grupo_de_operacao === 'Inventario'),
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      getInventarioById(selectedTp, inventarioId) {
        EstoqueHttp.getInventarioById(selectedTp, inventarioId)
          .then(inventario => {
            setState(prevState => ({
              ...prevState,
              inventario: inventario,
              inventarioFinished: inventario._status === 'Finalizado',
              loadingMessage: LOAD_MESSAGE.GET_POSICOES,
            }))
          })
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      getOperacoesSaida(selectedTp) {
        EstoqueHttp.getOperacoesEstoqueSaida(selectedTp)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              operacoesSaida: res.filter(r => r.grupo_de_operacao === 'Inventario'),
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      makeAcerto(selectedTp, body, inventarioId) {
        EstoqueHttp.acertoConferenciaProdutos(selectedTp, inventarioId, body)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.GET_POSICOES,
              inventarioFinished: true,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      //Actions
      onInit(selectedTp, selectedInventario) {
        if (selectedTp) {
          this.getInventarioById(selectedTp, selectedInventario)
        } else {
          setState(prevState => ({ ...prevState, errorMessage: 'Selecione um TP' }))
        }
      },

      onChange(name, value) {
        if (name === 'orderBy' || name === 'showFaltasSobras')
          setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.LOADING }))
        setState(prevState => {
          prevState[name] = value
          if (name === 'selectedDefaultOpSaida') {
            prevState.posicoes.forEach(p =>
              p.items.forEach(pr => {
                if (pr.qte_dif < 0) pr.operacao = value
              }),
            )
          } else if (name === 'selectedDefaultOpEntrada') {
            prevState.posicoes.forEach(p =>
              p.items.forEach(pr => {
                if (pr.qte_dif > 0) pr.operacao = value
              }),
            )
          } else if (name === 'orderBy') {
            switch (value.option) {
              case 'sequencia':
                prevState.posicoes = orderBy(prevState.posicoes, ['sequencia'], 'asc')
                break

              case 'mais_faltas':
                prevState.posicoes = orderBy(prevState.posicoes, ['total_faltas'], 'asc')
                break

              case 'mais_sobras':
                prevState.posicoes = orderBy(prevState.posicoes, ['total_sobras'], 'desc')
                break

              default:
                break
            }
            prevState.activeIndex = prevState.posicoes.map((p, i) => i)
            prevState.loadingMessage = LOAD_MESSAGE.NONE
          } else if (name === 'showFaltasSobras') {
            prevState.loadingMessage = LOAD_MESSAGE.NONE
          }
          return { ...prevState }
        })
      },

      isCheckedToImprimir(posicao, selectedPosicoes) {
        return selectedPosicoes.map(sp => sp.id).includes(posicao.id)
      },

      onChangeCheckbox(posicao) {
        setState(prevState => {
          const selecteds = prevState.selectedPosicoes.map(sp => sp.id)
          if (selecteds.includes(posicao.id))
            prevState.selectedPosicoes = prevState.selectedPosicoes.filter(
              sp => sp.id !== posicao.id,
            )
          else prevState.selectedPosicoes.push(posicao)
          return { ...prevState }
        })
      },

      onChangeProduto(posicaoId, produto, name, value) {
        setState(prevState => {
          const posicaoIndex = prevState.posicoes.findIndex(p => p.posicao_estoque_id === posicaoId)
          if (posicaoIndex !== -1) {
            const produtoIndex = prevState.posicoes[posicaoIndex].items.findIndex(
              prd =>
                prd.product_id === produto.product_id &&
                prd.data_validade === produto.data_validade &&
                prd.lote === produto.lote,
            )
            if (produtoIndex !== -1) {
              if (name === 'qte_ajuste_volume' || name === 'qte_ajuste_frac') {
                const item = prevState.posicoes[posicaoIndex].items[produtoIndex]
                item[name] = value
                const magnitude = getInventarioQuantityValueFromInputs(
                  item,
                  item.qte_ajuste_volume,
                  item.qte_ajuste_frac,
                )
                const sign = item.qte_dif < 0 ? -1 : 1
                let nextValue = magnitude === '' ? 0 : sign * Number(magnitude)
                if (Math.abs(nextValue) > Math.abs(item.qte_dif)) {
                  nextValue = item.qte_dif
                  const splitState = buildInventarioQuantityInputState(
                    item,
                    Math.abs(item.qte_dif),
                  )
                  item.qte_ajuste_volume = splitState.volumeValue
                  item.qte_ajuste_frac = splitState.unitsValue
                }
                item.qte_ajuste = nextValue
              } else if (name === 'qte_ajuste') {
                const produto = prevState.posicoes[posicaoIndex].items[produtoIndex]
                const valueAsNumber = Number(value)
                if (produto.qte_dif < 0) {
                  if (valueAsNumber > 0)
                    prevState.posicoes[posicaoIndex].items[produtoIndex][name] = 0
                  else if (valueAsNumber < produto.qte_dif)
                    prevState.posicoes[posicaoIndex].items[produtoIndex][name] = produto.qte_dif
                  else prevState.posicoes[posicaoIndex].items[produtoIndex][name] = value
                } else {
                  if (valueAsNumber < 0)
                    prevState.posicoes[posicaoIndex].items[produtoIndex][name] = 0
                  else if (valueAsNumber > produto.qte_dif)
                    prevState.posicoes[posicaoIndex].items[produtoIndex][name] = produto.qte_dif
                  else prevState.posicoes[posicaoIndex].items[produtoIndex][name] = value
                }
              } else prevState.posicoes[posicaoIndex].items[produtoIndex][name] = value
            }
          }
          return { ...prevState }
        })
      },

      resetState() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },

      onClickImprimir(selectedTp, inventarioId) {
        EstoqueHttp.imprimirPosicoesConferencia(selectedTp, inventarioId)
      },

      onExportarExcel(posicoes) {
        if (!Array.isArray(posicoes)) return
        if (!posicoes.length) return

        const data = [
          [
            'posicao_estoque_id',
            'sequencia',
            'endereco',
            'data_validade',
            'lote',
            'produto_codigo',
            'produto_nome',
            'qte_sistema',
            'qte_inventario',
            'qte_dif',
            'qte_ajuste',
            'qte_sistema_vol_frac',
            'qte_inventario_vol_frac',
            'qte_dif_vol_frac',
            'qte_ajuste_vol_frac',
          ],
        ]
        posicoes.forEach(p => {
          const { posicao_estoque_id, sequencia, endereco } = p
          if (Array.isArray(p.items) && p.items.length > 0) {
            p.items.forEach(i => {
              data.push([
                posicao_estoque_id,
                sequencia,
                endereco,
                i.data_validade,
                i.lote,
                i.product_code,
                i.product_name,
                i.qte_sistema,
                i.qte_inventory,
                i.qte_dif,
                i.qte_ajuste,
                formatInventarioQuantity(i, i.qte_sistema),
                formatInventarioQuantity(i, i.qte_inventory),
                formatInventarioQuantity(i, i.qte_dif),
                formatInventarioQuantity(i, i.qte_ajuste),
              ])
            })
          } else {
            data.push([
              posicao_estoque_id,
              sequencia,
              endereco,
              '-',
              '-',
              '-',
              '-',
              '-',
              '-',
              '-',
              '-',
              '-',
              '-',
              '-',
              '-',
            ])
          }
        })
        gerarArquivoExcel('conferências', data, 'analise_resultado.xlsx')
      },

      onClickAcertoOpenModal() {
        setState(prevState => ({ ...prevState, showDialogConfirm: true }))
      },

      onClickAcertoCloseModal() {
        setState(prevState => ({ ...prevState, showDialogConfirm: false }))
      },

      onClickAcerto() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.OK_CLICK_ACERTO,
        }))
      },

      onClickAcertoSave(selectedTp, posicoes) {
        posicoes.forEach(p => {
          p.items.forEach(i => (i.posicao_estoque_id = p.posicao_estoque_id))
        })

        const items = posicoes.map(p => p.items).reduce((prev, curr) => prev.concat(curr), [])
        items.forEach(i => {
          if (i.data_validade) {
            i.data_validade_formated = parseToMysqlFormat(i.data_validade)
          }
        })
        const operacoes = new Set(items.filter(i => i.operacao).map(i => i.operacao))

        const body = []

        if (items.some(i => !i.operacao && Number(i.qte_ajuste) !== 0))
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Existem itens que precisam ter uma operação de movimentação associada.',
          }))
        else {
          operacoes.forEach(op => {
            const itemsForOp = items
              .filter(i => i.operacao && i.operacao.id === op.id)
              .map(i => ({ ...i }))
            itemsForOp.forEach(i => {
              if (i.data_validade) {
                i.data_validade = i.data_validade_formated
              }
            })
            if (itemsForOp.length) {
              body.push({
                operacao_id: op.id,
                operacao_type: op.type,
                items: itemsForOp,
              })
            }
          })

          const itemsWithoutOp = items.filter(i => !i.operacao).map(i => ({ ...i }))

          itemsWithoutOp.forEach(i => {
            if (i.data_validade) {
              i.data_validade = i.data_validade_formated
            }
          })
          if (itemsWithoutOp.length) {
            body.push({
              operacao_id: null,
              operacao_type: null,
              items: itemsWithoutOp,
            })
          }

          setState(prevState => ({
            ...prevState,
            body: body,
            showDialogConfirm: false,
            loadingMessage: LOAD_MESSAGE.ACERTO,
          }))
        }
      },

      onClickOpenNovaConferenciaModal(posicao) {
        setState(prevState => ({
          ...prevState,
          showNovaConferenciaModal: true,
          selectedPosicao: posicao,
        }))
      },

      onClickCloseNovaConferenciaModal() {
        setState(prevState => ({
          ...prevState,
          showNovaConferenciaModal: false,
          selectedPosicao: null,
        }))
      },

      onClickConfirmNovaConferencia() {
        setState(prevState => ({
          ...prevState,
          showNovaConferenciaModal: false,
          loadingMessage: LOAD_MESSAGE.NOVA_CONFERENCIA,
        }))
      },

      createConferenciaFromPosicao(selectedTp, selectedInventarioId, selectedPosicaoId) {
        EstoqueHttp.createConferenciaInventarioFromPosicoes(selectedTp, selectedInventarioId, [
          selectedPosicaoId,
        ])
          .then(res => {
            setState(prevState => ({
              ...prevState,
              successMessage: `Conferencia gerada com sucesso`,
              posicoes: res,
              selectedPosicao: null,
              loadingMessage: LOAD_MESSAGE.GET_POSICOES,
            }))
          })
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },
    }
  }, [])

  return { state, localDispatch }
}
